import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Chip,
  Card,
  CardContent,
  CardActions,
  Alert,
  Collapse,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  PlayArrow as ExecuteIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import {
  getExecutionStatusInfo,
  formatExecutionTime,
} from '../utils/executionUtils';
import { debugLog } from '../../../utils/debugLog';

const ResultsPanel = ({
  executions,
  selectedExecution,
  setSelectedExecution,
  onLoadExecutions,
  onOpenHistoryDialog,
  executing = false,
  executionStatus = null,
  executionProgress = null,
}) => {
  const [historyExpanded, setHistoryExpanded] = useState(false);
  debugLog('🔧 ResultsPanel: historyExpanded =', historyExpanded);
  const selectedExecutionData = executions.find(
    e => e.id === selectedExecution
  );

  return (
    <>
      {/* Latest Results */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <ExecuteIcon sx={{ mr: 1 }} />
          Latest Results
        </Typography>

        {executing && !selectedExecutionData ? (
          <Box textAlign="center" py={4}>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {executionStatus || 'Template is executing...'}
            </Typography>
            {executionProgress?.estimatedSecondsRemaining && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Estimated time remaining: ~
                {executionProgress.estimatedSecondsRemaining}s
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              Please wait while we process your request. Results will appear
              here when complete.
            </Typography>
          </Box>
        ) : selectedExecutionData && executing ? (
          <Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                Execution {selectedExecutionData.id.slice(-8)}
              </Typography>
              <Chip label="Processing..." color="info" size="small" />
            </Box>
            <Box sx={{ mb: 2 }}>
              <CircularProgress
                size={20}
                sx={{ mr: 1, verticalAlign: 'middle' }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                component="span"
              >
                {executionStatus || 'Processing execution...'}
              </Typography>
              {executionProgress?.estimatedSecondsRemaining && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mt: 1 }}
                >
                  Estimated time remaining: ~
                  {executionProgress.estimatedSecondsRemaining}s
                </Typography>
              )}
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mb={2}
            >
              {new Date(selectedExecutionData.created_at).toLocaleString()} |
              Time: {selectedExecutionData.execution_time?.toFixed(2) || 'N/A'}s
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Execution is in progress. Results will update automatically when
                complete.
              </Typography>
            </Alert>
          </Box>
        ) : selectedExecutionData ? (
          <Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                Execution {selectedExecutionData.id.slice(-8)}
              </Typography>
              <Chip
                label={selectedExecutionData.success ? 'Success' : 'Failed'}
                color={selectedExecutionData.success ? 'success' : 'error'}
                size="small"
              />
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mb={2}
            >
              {new Date(selectedExecutionData.created_at).toLocaleString()} |
              Time: {selectedExecutionData.execution_time?.toFixed(2) || 'N/A'}s
            </Typography>

            {selectedExecutionData.error_message && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  {selectedExecutionData.error_code || 'Execution Error'}
                </Typography>
                <Typography variant="body2">
                  {selectedExecutionData.error_message}
                </Typography>
                {selectedExecutionData.validation_errors &&
                  selectedExecutionData.validation_errors.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        display="block"
                        gutterBottom
                      >
                        Validation Errors:
                      </Typography>
                      {selectedExecutionData.validation_errors.map(
                        (error, idx) => (
                          <Typography
                            key={idx}
                            variant="caption"
                            component="div"
                            sx={{ display: 'block', mt: 0.5 }}
                          >
                            • {error.message || JSON.stringify(error)}
                          </Typography>
                        )
                      )}
                    </Box>
                  )}
              </Alert>
            )}

            <Typography variant="subtitle2" gutterBottom>
              Raw Response:
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              value={selectedExecutionData.raw_response || 'No response'}
              InputProps={{ readOnly: true }}
              size="small"
              sx={{ mb: 2 }}
            />

            {(() => {
              // Debug logging
              debugLog('🔍 ResultsPanel - selectedExecutionData:', {
                id: selectedExecutionData?.id,
                has_structured_output:
                  selectedExecutionData?.structured_output != null,
                structured_output_type:
                  typeof selectedExecutionData?.structured_output,
                structured_output_value:
                  selectedExecutionData?.structured_output,
                has_output_data: selectedExecutionData?.output_data != null,
                output_data_type: typeof selectedExecutionData?.output_data,
                output_format: selectedExecutionData?.output_format,
              });

              return selectedExecutionData.structured_output != null ||
                selectedExecutionData.output_data != null ? (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Structured Output:
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={(() => {
                      // Use structured_output first, fallback to output_data
                      const output =
                        selectedExecutionData.structured_output ??
                        selectedExecutionData.output_data;
                      const outputFormat =
                        selectedExecutionData.output_format || 'text';
                      debugLog('🔍 ResultsPanel - Displaying output:', {
                        output,
                        outputFormat,
                        outputType: typeof output,
                      });

                      // Handle null/undefined
                      if (output == null) {
                        return '';
                      }

                      // If output is already a string
                      if (typeof output === 'string') {
                        // Check if it's the problematic "[object Object]" string
                        if (
                          output === '[object Object]' ||
                          output === '[object Array]'
                        ) {
                          // Try to get the original object from the data
                          const originalOutput =
                            selectedExecutionData.structured_output ??
                            selectedExecutionData.output_data;
                          if (
                            originalOutput &&
                            typeof originalOutput === 'object'
                          ) {
                            try {
                              return JSON.stringify(originalOutput, null, 2);
                            } catch (e) {
                              return output;
                            }
                          }
                        }
                        return output;
                      }

                      // If output is an object or array, stringify it
                      if (typeof output === 'object' && output !== null) {
                        try {
                          return JSON.stringify(output, null, 2);
                        } catch (e) {
                          // If stringification fails, try to get a better representation
                          if (Array.isArray(output)) {
                            return `[Array(${output.length})]`;
                          }
                          return `[Object with ${Object.keys(output).length} keys]`;
                        }
                      }

                      // For other types, convert to string
                      return String(output);
                    })()}
                    InputProps={{ readOnly: true }}
                    size="small"
                    sx={{ mb: 2 }}
                  />
                </>
              ) : null;
            })()}

            <Typography variant="subtitle2" gutterBottom>
              Model Used: {selectedExecutionData.model_used || 'N/A'}
            </Typography>
          </Box>
        ) : (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              Execute the template to see results here.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Execution History */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={historyExpanded ? 2 : 0}
        >
          <Box
            display="flex"
            alignItems="center"
            sx={{ cursor: 'pointer' }}
            onClick={() => setHistoryExpanded(!historyExpanded)}
          >
            <IconButton size="small" sx={{ mr: 0.5 }}>
              {historyExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <Typography
              variant="h6"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <HistoryIcon sx={{ mr: 1 }} />
              Execution History
            </Typography>
          </Box>
          <Box>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={onLoadExecutions}
              variant="outlined"
              sx={{ mr: 1 }}
            >
              Refresh
            </Button>
            <Button
              size="small"
              startIcon={<HistoryIcon />}
              onClick={onOpenHistoryDialog}
              variant="contained"
            >
              View All
            </Button>
          </Box>
        </Box>

        <Collapse in={historyExpanded}>
          {!executions || executions.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="text.secondary">
                No executions yet. Execute the template to see results here.
              </Typography>
            </Box>
          ) : (
            <Box>
              {executions.map(execution => {
                const statusInfo = getExecutionStatusInfo(execution);
                const isSelected = selectedExecution === execution.id;

                return (
                  <Card
                    key={execution.id}
                    sx={{
                      mb: 2,
                      border: '1px solid',
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      backgroundColor: isSelected
                        ? 'primary.50'
                        : 'background.paper',
                      cursor: 'pointer',
                    }}
                    onClick={() => setSelectedExecution(execution.id)}
                  >
                    <CardContent sx={{ pb: 1 }}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        mb={1}
                      >
                        <Typography variant="subtitle2" fontWeight={600}>
                          Execution {execution.id.slice(-8)}
                        </Typography>
                        <Chip
                          label={statusInfo.label}
                          color={statusInfo.color}
                          size="small"
                        />
                      </Box>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {new Date(execution.created_at).toLocaleString()} |
                        Time: {formatExecutionTime(execution)}
                      </Typography>

                      {execution.error_message && (
                        <Alert
                          severity="error"
                          sx={{ mt: 1, fontSize: '0.75rem' }}
                        >
                          <Typography
                            variant="caption"
                            fontWeight="bold"
                            display="block"
                          >
                            {execution.error_code || 'Error'}
                          </Typography>
                          <Typography variant="caption" component="div">
                            {execution.error_message}
                          </Typography>
                          {execution.validation_errors &&
                            execution.validation_errors.length > 0 && (
                              <Typography
                                variant="caption"
                                component="div"
                                sx={{ mt: 0.5 }}
                              >
                                {execution.validation_errors.length} validation
                                error(s)
                              </Typography>
                            )}
                        </Alert>
                      )}
                    </CardContent>

                    {isSelected && (
                      <CardActions sx={{ pt: 0 }}>
                        <Box sx={{ width: '100%' }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Raw Response:
                          </Typography>
                          <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={execution.raw_response || 'No response'}
                            InputProps={{ readOnly: true }}
                            size="small"
                            sx={{ mb: 2 }}
                          />

                          {(execution.structured_output != null ||
                            execution.output_data != null) && (
                            <>
                              <Typography variant="subtitle2" gutterBottom>
                                Structured Output:
                              </Typography>
                              <TextField
                                fullWidth
                                multiline
                                rows={3}
                                value={(() => {
                                  // Use structured_output first, fallback to output_data
                                  const output =
                                    execution.structured_output ??
                                    execution.output_data;
                                  const outputFormat =
                                    execution.output_format || 'text';

                                  // Handle null/undefined
                                  if (output == null) {
                                    return '';
                                  }

                                  // If output is already a string
                                  if (typeof output === 'string') {
                                    // Check if it's the problematic "[object Object]" string
                                    if (
                                      output === '[object Object]' ||
                                      output === '[object Array]'
                                    ) {
                                      // Try to get the original object from the data
                                      const originalOutput =
                                        execution.structured_output ??
                                        execution.output_data;
                                      if (
                                        originalOutput &&
                                        typeof originalOutput === 'object'
                                      ) {
                                        try {
                                          return JSON.stringify(
                                            originalOutput,
                                            null,
                                            2
                                          );
                                        } catch (e) {
                                          return output;
                                        }
                                      }
                                    }
                                    return output;
                                  }

                                  // If output is an object or array, stringify it
                                  if (
                                    typeof output === 'object' &&
                                    output !== null
                                  ) {
                                    try {
                                      return JSON.stringify(output, null, 2);
                                    } catch (e) {
                                      // If stringification fails, try to get a better representation
                                      if (Array.isArray(output)) {
                                        return `[Array(${output.length})]`;
                                      }
                                      return `[Object with ${Object.keys(output).length} keys]`;
                                    }
                                  }

                                  // For other types, convert to string
                                  return String(output);
                                })()}
                                InputProps={{ readOnly: true }}
                                size="small"
                                sx={{ mb: 2 }}
                              />
                            </>
                          )}

                          <Box display="flex" gap={1}>
                            <Button
                              size="small"
                              startIcon={<ThumbUpIcon />}
                              variant="outlined"
                              color="success"
                            >
                              Good
                            </Button>
                            <Button
                              size="small"
                              startIcon={<ThumbDownIcon />}
                              variant="outlined"
                              color="error"
                            >
                              Bad
                            </Button>
                          </Box>
                        </Box>
                      </CardActions>
                    )}
                  </Card>
                );
              })}
            </Box>
          )}
        </Collapse>
      </Paper>
    </>
  );
};

export default ResultsPanel;
